import type { AnalyticsEventRequestItem } from '@resistapp/common/api-types';
import type { AnalyticsLocationSnapshot } from './analytics-utils';

export type PendingAnalyticsLocationEvent = {
  interactionId: number;
  event: AnalyticsEventRequestItem;
};

interface RequeueAnalyticsEventsAfterFailureArgs {
  currentQueuedEvents: AnalyticsEventRequestItem[];
  currentStateVersion: number;
  failedEvents: AnalyticsEventRequestItem[];
  flushStateVersion: number;
  maxQueuedEvents: number;
}

interface AppendPendingLocationEventToQueueArgs {
  currentQueuedEvents: AnalyticsEventRequestItem[];
  maxQueuedEvents: number;
  pendingLocationEvent: PendingAnalyticsLocationEvent | null;
}

interface ShouldStartAnalyticsFlushArgs {
  activeFlushCount: number;
  analyticsDisabled: boolean;
  hasQueuedEvents: boolean;
  keepalive: boolean;
  sessionId: string | null;
}

export function didAnalyticsIdentityChange(
  previousUserId: number | null | undefined,
  nextUserId: number | null | undefined,
): boolean {
  if (previousUserId === undefined || nextUserId === undefined) {
    return false;
  }

  return previousUserId !== nextUserId;
}

export class AnalyticsUploadError extends Error {
  status: number;

  constructor(status: number) {
    super(`Analytics event upload failed with status ${status}`);
    this.status = status;
  }
}

export function isTerminalAnalyticsUploadFailure(status: number): boolean {
  return status === 401 || status === 403;
}

export function isCurrentAnalyticsFlushState(currentStateVersion: number, flushStateVersion: number): boolean {
  return currentStateVersion === flushStateVersion;
}

export function parseStoredAnalyticsLocation(rawValue: string | null): AnalyticsLocationSnapshot | null {
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<AnalyticsLocationSnapshot>;
    if (typeof parsedValue.pathname !== 'string' || typeof parsedValue.search !== 'string') {
      return null;
    }
    return {
      pathname: parsedValue.pathname,
      search: parsedValue.search,
    };
  } catch {
    return null;
  }
}

export function serialiseAnalyticsLocation(location: AnalyticsLocationSnapshot): string {
  return JSON.stringify(location);
}

export function shouldStartAnalyticsFlush({
  activeFlushCount,
  analyticsDisabled,
  hasQueuedEvents,
  keepalive,
  sessionId,
}: ShouldStartAnalyticsFlushArgs): boolean {
  if (!sessionId || analyticsDisabled || !hasQueuedEvents) {
    return false;
  }

  return keepalive || activeFlushCount === 0;
}

export function coalescePendingLocationEvent(
  previousPendingEvent: PendingAnalyticsLocationEvent | null,
  nextPendingEvent: PendingAnalyticsLocationEvent,
): PendingAnalyticsLocationEvent {
  if (!previousPendingEvent || previousPendingEvent.interactionId !== nextPendingEvent.interactionId) {
    return nextPendingEvent;
  }

  const previousActionType = previousPendingEvent.event.actionType;
  const nextActionType = nextPendingEvent.event.actionType;
  const shouldPreserveEntryAction =
    (previousActionType === 'navigation' || previousActionType === 'landing') && nextActionType === 'query';
  if (!shouldPreserveEntryAction) {
    return nextPendingEvent;
  }

  return {
    interactionId: nextPendingEvent.interactionId,
    event: {
      ...previousPendingEvent.event,
      path: nextPendingEvent.event.path,
      query: nextPendingEvent.event.query,
    },
  };
}

export function appendPendingLocationEventToQueue({
  currentQueuedEvents,
  maxQueuedEvents,
  pendingLocationEvent,
}: AppendPendingLocationEventToQueueArgs): AnalyticsEventRequestItem[] {
  if (!pendingLocationEvent) {
    return currentQueuedEvents;
  }

  return [...currentQueuedEvents, pendingLocationEvent.event].slice(-maxQueuedEvents);
}

export function requeueAnalyticsEventsAfterFailure({
  currentQueuedEvents,
  currentStateVersion,
  failedEvents,
  flushStateVersion,
  maxQueuedEvents,
}: RequeueAnalyticsEventsAfterFailureArgs): AnalyticsEventRequestItem[] {
  if (!isCurrentAnalyticsFlushState(currentStateVersion, flushStateVersion)) {
    return currentQueuedEvents;
  }

  return [...failedEvents, ...currentQueuedEvents].slice(-maxQueuedEvents);
}
